import React, { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "preparing", "ready", "completed", "cancelled"];
const COLORS = {
  new: "bg-yellow-400/10 text-yellow-300 border-yellow-400/40",
  preparing: "bg-blue-400/10 text-blue-300 border-blue-400/40",
  ready: "bg-green-400/10 text-green-300 border-green-400/40",
  completed: "bg-[#3D1A21] text-[#D1C7C9] border-[#666]",
  cancelled: "bg-red-400/10 text-red-300 border-red-400/40",
};

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/orders/${id}/status`, null, { params: { status } });
    toast.success("Updated");
    load();
  };

  const printKitchen = (o) => {
    const win = window.open("", "_blank", "width=400,height=700");
    if (!win) return toast.error("Pop-up blocked");
    const dt = new Date(o.created_at);
    const printTime = new Date().toLocaleString("en-GB", { hour12: false });
    const totalQty = o.items.reduce((s, i) => s + i.quantity, 0);
    const txn = o.id.slice(0, 8).toUpperCase();
    const itemsHtml = o.items.map(i => `
      <tr>
        <td style="padding:4px 0;border-bottom:1px dashed #999;">
          <div style="font-size:14px;font-weight:bold;">${i.name}</div>
        </td>
        <td style="text-align:center;padding:4px 0;border-bottom:1px dashed #999;font-size:14px;">${i.quantity}</td>
        <td style="text-align:right;padding:4px 0;border-bottom:1px dashed #999;font-size:14px;">${(i.price * i.quantity).toFixed(0)}</td>
      </tr>`).join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Kitchen Receipt - Table ${o.table_number}</title>
    <style>
      @media print { @page { size: 80mm auto; margin: 4mm; } body { margin: 0; } }
      body { font-family: 'Courier New', 'Microsoft JhengHei', monospace; padding: 8px; max-width: 320px; margin: 0 auto; color: #000; background: #fff; }
      .center { text-align: center; }
      h1 { margin: 0; font-size: 18px; letter-spacing: 1px; }
      .sub { font-size: 11px; margin: 2px 0; }
      .divider { border-top: 2px solid #000; margin: 6px 0; }
      .dashed { border-top: 1px dashed #666; margin: 6px 0; }
      .info { font-size: 11px; margin: 2px 0; }
      .table-box { text-align: center; font-size: 28px; font-weight: bold; padding: 6px; border: 2px solid #000; margin: 8px 0; }
      table { width: 100%; border-collapse: collapse; margin-top: 4px; }
      th { font-size: 11px; text-align: left; padding: 4px 0; border-bottom: 1px solid #000; }
      th.c { text-align: center; } th.r { text-align: right; }
      .totals { font-size: 13px; padding: 4px 0; }
      .grand { font-size: 16px; font-weight: bold; padding: 6px 0; border-top: 2px solid #000; border-bottom: 2px solid #000; }
      .receipt-label { text-align: center; background: #000; color: #fff; padding: 4px 0; font-size: 13px; font-weight: bold; letter-spacing: 2px; margin-top: 6px; }
      .note { font-style: italic; font-size: 12px; padding: 6px; border: 1px dashed #999; margin-top: 6px; }
    </style></head><body>
      <div class="center">
        <h1>杜爾希印度餐廳</h1>
        <h1 style="margin-top:2px;">TULSI Indian Restaurant</h1>
        <div class="sub">${o.customer_phone || "0905344045"}</div>
      </div>
      <div class="divider"></div>
      <div class="info">交易序號 / Order #: <strong>${txn}</strong></div>
      <div class="info">列印時間 / Print Time: ${printTime}</div>
      <div class="info">點餐時間 / Order Time: ${dt.toLocaleString("en-GB", { hour12: false })}</div>
      <div class="info">顧客 / Customer: <strong>${o.customer_name || "Guest"}</strong></div>
      <div class="table-box">${o.table_number}桌 · Table #${o.table_number}</div>
      <table>
        <thead><tr>
          <th>品項 / Item</th>
          <th class="c">數量<br>Qty</th>
          <th class="r">金額<br>Amt</th>
        </tr></thead>
        <tbody>${itemsHtml}</tbody>
      </table>
      <div class="dashed"></div>
      <table>
        <tr class="totals"><td>總數量 / Total Qty</td><td class="r"><strong>${totalQty}</strong></td></tr>
        <tr class="totals"><td>小計 / Subtotal</td><td class="r">NT$ ${o.subtotal.toFixed(0)}</td></tr>
      </table>
      <div class="grand" style="display:flex;justify-content:space-between;">
        <span>銷售總額 / TOTAL</span>
        <span>NT$ ${o.total.toFixed(0)}</span>
      </div>
      <div class="totals" style="display:flex;justify-content:space-between;margin-top:4px;">
        <span>現金 / Cash</span>
        <span>NT$ ${o.total.toFixed(0)}</span>
      </div>
      ${o.notes ? `<div class="note"><strong>備註 / NOTE:</strong> ${o.notes}</div>` : ""}
      <div class="receipt-label">收  據 · RECEIPT</div>
      <div class="center sub" style="margin-top:6px;">謝謝光臨 · Thank You!</div>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500);}</script>
    </body></html>`;
    win.document.write(html);
    win.document.close();
  };

  return (
    <div data-testid="orders-admin">
      <h1 className="font-serif-display text-4xl text-gold-gradient mb-2">QR Orders</h1>
      <p className="text-sm text-[#D1C7C9] mb-6">Live orders from tables (auto-refresh every 10s)</p>

      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="bg-[#1A1A1A] border border-[#333] rounded-xl p-5" data-testid={`order-${o.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-serif-display text-3xl text-[#E69138]">#{o.table_number}</span>
                  <span className={`text-[10px] tracking-widest uppercase border px-2 py-0.5 rounded ${COLORS[o.status]}`}>{o.status}</span>
                </div>
                <p className="text-xs text-[#D1C7C9] mt-1">{o.customer_name} · {new Date(o.created_at).toLocaleString()}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => printKitchen(o)}
                  className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  data-testid={`print-receipt-${o.id}`}
                >
                  <Printer size={11} className="inline mr-1" /> Print Receipt
                </button>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(o.id, s)}
                    disabled={o.status === s}
                    className={`text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border ${COLORS[s]} ${o.status === s ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}
                    data-testid={`order-${o.id}-status-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-4 items-start">
              <ul className="text-sm space-y-1">
                {o.items.map((i, idx) => (
                  <li key={idx} className="flex justify-between text-[#D1C7C9]">
                    <span>{i.quantity} × {i.name}</span>
                    <span className="text-[#FDFBF7]">NT$ {(i.price * i.quantity).toFixed(0)}</span>
                  </li>
                ))}
              </ul>
              <div className="text-right">
                <p className="text-xs uppercase tracking-widest text-[#D1C7C9]/60">Total</p>
                <p className="font-serif-display text-2xl text-[#D4AF37]">NT$ {o.total}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#E69138] mt-1">Cash</p>
              </div>
            </div>
            {o.notes && <p className="mt-3 text-xs text-[#D1C7C9] italic border-l-2 border-[#D4AF37] pl-3">Note: {o.notes}</p>}
          </div>
        ))}
        {orders.length === 0 && <p className="text-[#D1C7C9]">No orders yet.</p>}
      </div>
    </div>
  );
}
